// Sidebar.jsx
import React from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import HamburgerButton from '../components/Helpers/hamburger-button';
import HomeIcon from "../Images/home-icon.svg";
import ConversationsIcon from "../Images/conversations-icon.svg";
import LogoutIcon from "../Images/logout-icon.svg";
import { useAuth0 } from "@auth0/auth0-react";

const SidebarContainer = styled.div`
  width: ${props => props.$expanded ? '120px' : '60px'};
  min-width: ${props => props.$expanded ? '120px' : '60px'};
  flex-shrink: 0;
  height: 100%;
  background: #ffffff;
  transition: width 0.3s ease;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e0e8f5;
  position: relative;
  z-index: 100;
  
  @media (max-width: 768px) {
    width: 100%;
    min-width: 100%;
    flex-direction: row;
    justify-content: space-between;
    height: 65px;
    order: 3;
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: 1000;
    background: rgba(255, 255, 255, 0.94);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-top: 1px solid #e0e8f5;
    border-right: none;
    box-shadow: 0px -2px 10px rgba(39, 68, 131, 0.088);
  }
`;

const HamburgerContainer = styled.div`
  padding: ${props => props.$expanded ? '12px 8px' : '12px 0'};
  display: flex;
  justify-content: ${props => props.$expanded ? 'flex-start' : 'center'};
  align-items: center;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const Menu = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  
  @media (max-width: 768px) {
    display: flex;
    width: 100%;
    justify-content: space-around;
  }
`;

const MenuItem = styled.li`
  display: flex;
  align-items: center;
  height: 50px;
  color: ${props => props.$active ? '#335cf4' : '#5c6b83'};
  cursor: pointer;
  transition: all 0.3s;
  justify-content: ${props => props.$expanded ? 'flex-start' : 'center'};
  padding-left: ${props => props.$expanded ? '10px' : '0'};
  background-color: transparent;
  position: relative;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background-color: transparent;
  }

  &:active {
    background-color: transparent;
  }

  img {
    filter: ${props => props.$active
      ? 'brightness(0) saturate(100%) invert(22%) sepia(94%) saturate(2473%) hue-rotate(225deg) brightness(102%) contrast(91%)'
      : 'brightness(0) saturate(100%) invert(34%) sepia(15%) saturate(1133%) hue-rotate(179deg) brightness(88%) contrast(88%)'};
    opacity: 1;
    transition: opacity 0.3s;

    &:hover {
      opacity: 1;
    }
  }
  
  @media (max-width: 768px) {
    justify-content: center;
    padding: 10px;
    padding-left: 10px;
    height: auto;
    flex-direction: column;
    transition: all 0.3s ease;
    background-color: transparent;
    
    &:hover, &:active, &:focus {
      background-color: transparent;
    }
    
    &::after {
      content: '';
      display: block;
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: ${props => props.$active ? '30px' : '0'};
      height: 3px;
      background-color: #335cf4;
      border-radius: 4px;
      transition: width 0.3s ease;
      opacity: ${props => props.$active ? '1' : '0'};
    }
    
    &::before {
      content: none;
    }
    
    &.logout-item {
      img {
        filter: brightness(0) saturate(100%) invert(34%) sepia(15%) saturate(1133%) hue-rotate(179deg) brightness(88%) contrast(88%) !important;
      }
      
      &:hover, &:active, &:focus {
        background-color: transparent;
      }
    }
  }
`;

const MenuIcon = styled.div`
  margin: 0 5px 0 0;
  width: 22px;
  height: 22px;
  -webkit-tap-highlight-color: transparent;
  background-color: transparent;
  
  &:hover, &:active, &:focus {
    background-color: transparent;
  }
  
  @media (max-width: 768px) {
    width: 24px;
    height: 24px;
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: transparent;
    
    &:hover, &:active, &:focus {
      background-color: transparent;
    }
    
    img {
      transition: all 0.3s ease;
      filter: ${props => props.$active ? 'brightness(0) saturate(100%) invert(22%) sepia(94%) saturate(2473%) hue-rotate(225deg) brightness(102%) contrast(91%)' : 'brightness(0) saturate(100%) invert(34%) sepia(15%) saturate(1133%) hue-rotate(179deg) brightness(88%) contrast(88%)'};
      opacity: ${props => props.$active ? '1' : '0.7'};
      width: ${props => props.$active ? '92%' : '85%'};
      height: ${props => props.$active ? '92%' : '85%'};
    }
  }
`;

const MenuText = styled.span`
  display: ${props => props.$expanded ? 'inline' : 'none'};
  color: ${props => props.$active ? '#335cf4' : '#5c6b83'};
  font-size: 13px;
  font-weight: ${props => props.$active ? '600' : '500'};
  margin-left: 2px;
  
  @media (max-width: 768px) {
    display: block;
    font-size: 10px;
    margin-top: 4px;
    color: ${props => props.$active ? '#335cf4' : '#65748d'};
    font-weight: ${props => props.$active ? '500' : 'normal'};
    transition: all 0.3s ease;
  }
`;

const Sidebar = ({ expanded, onExpandToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth0();
  
  const isActive = (path) => {
    if (path === '/') {
      // For home route, check if it's exactly the home path
      return location.pathname === '/' || location.pathname === '/home';
    } else if (path === '/conversations') {
      // For conversations, check if the path starts with /conversations
      return location.pathname.startsWith('/conversations');
    }
    // For other routes, use includes
    return location.pathname.includes(path);
  };

  const NavigateToPage = (where) => () => {
    if (where === '/conversations' && window.innerWidth <= 768) {
      navigate('/conversations?toggle=true', { replace: true });
    } else {
      navigate(where, { replace: true });
    }
  };

  return (
    <SidebarContainer $expanded={expanded}>
      <HamburgerContainer $expanded={expanded}>
        <HamburgerButton toggle={onExpandToggle} isOpen={expanded} />
      </HamburgerContainer>

      <Menu>
        <MenuItem 
          onClick={NavigateToPage('/')} 
          $expanded={expanded}
          $active={isActive('/')}
        >
          <MenuIcon $active={isActive('/')}>
            <img width={'90%'} height={'90%'} alt='dashboard icon' src={HomeIcon} />
          </MenuIcon>
          <MenuText $expanded={expanded} $active={isActive('/')}>Home</MenuText>
        </MenuItem>

        <MenuItem 
          onClick={NavigateToPage('/conversations')} 
          $expanded={expanded}
          $active={isActive('/conversations')}
        >
          <MenuIcon $active={isActive('/conversations')}>
            <img width={'90%'} height={'90%'} alt='users icon' src={ConversationsIcon} />
          </MenuIcon>
          <MenuText $expanded={expanded} $active={isActive('/conversations')}>Convo</MenuText>
        </MenuItem>

        <MenuItem 
          onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })} 
          $expanded={expanded}
          className="logout-item"
        >
          <MenuIcon>
            <img width={'90%'} height={'90%'} alt='logout icon' src={LogoutIcon} />
          </MenuIcon>
          <MenuText $expanded={expanded}>Logout</MenuText>
        </MenuItem>
      </Menu>
    </SidebarContainer>
  );
};

export default Sidebar;
